import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Moment } from "moment-timezone";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useState } from "react";
import { DelaysParetoGraphic } from "./components/DelaysParetoGraphic";
import { Filters } from "./components/filters/Filters";
import { DelayForPareto } from "./models/DelayForPareto";
import { getDelaysForPareto } from "./repositories/DelaysParetoReportRepository";

const moment = getMomentTz();

type useDelaysParetoProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeUndeclared: boolean;
	onError: (error: Error) => void;
};

export const useDelaysPareto = ({
	startDate,
	endDate,
	delayAreaAssetId,
	includeUndeclared,
	onError,
}: useDelaysParetoProps) => {
	return useQuery<DelayForPareto[], Error>({
		queryKey: [
			"delaysPareto",
			startDate,
			endDate,
			delayAreaAssetId,
			includeUndeclared,
		],
		queryFn: () =>
			getDelaysForPareto({
				startDate,
				endDate,
				delayAreaAssetId,
				includeUndeclared,
			}),
		onError: onError,
	});
};

const URL_SCHEMA = {
	start: urlParam.date(() => moment().subtract(7, "days").startOf("day"), {
		moment,
	}),
	end: urlParam.date(() => moment().endOf("day"), { moment }),
	includeUndeclared: urlParam.bool(true),
};

export const DelaysParetoReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [
		{ start: filterStartDate, end: filterEndDate, includeUndeclared },
		setFilters,
		{ resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		null,
	);
	const includeUndeclaredDelays = includeUndeclared;

	const { data: delaysPareto, isFetching } = useDelaysPareto({
		startDate: filterStartDate.clone().startOf("day").toDate(),
		// The URL carries a calendar date, so the end has to be widened back to the end of
		// that day or a link would cover one day less than the picker shows.
		endDate: filterEndDate.clone().endOf("day").toDate(),
		delayAreaAssetId: selectedDelayArea,
		includeUndeclared: includeUndeclaredDelays,
		onError: (e) => setError(e.message),
	});

	// The picker reports a half-typed date as an invalid Moment (year 0202 on the way to
	// 2026); writing it would park it in the URL.
	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilters({
			...(isUsableDate(startDate) ? { start: startDate } : {}),
			...(isUsableDate(endDate) ? { end: endDate } : {}),
		});
	};

	const handleDelayAreaChange = (assetId: number | null) => {
		setSelectedDelayArea(assetId);
	};

	return (
		<>
			<HelmetDexteel title="Pareto Report" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					p: 1,
					bgcolor: "#f0f2f5",
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Pareto Report
				</Typography>
				<Paper elevation={3}>
					<Filters
						onReset={() => resetValues()}
						canReset={!isDefault()}
						onFilterChange={handleFilterChange}
						initialStartDate={filterStartDate}
						initialEndDate={filterEndDate}
						selectedDelayArea={selectedDelayArea}
						onDelayAreaChange={handleDelayAreaChange}
						includeUndeclaredDelays={includeUndeclaredDelays}
						onIncludeUndeclaredDelaysChange={(value) =>
							setFilters({ includeUndeclared: value })
						}
					/>
				</Paper>

				{isFetching ? (
					<CenteredLazyLoading />
				) : (
					<DelaysParetoGraphic delaysForPareto={delaysPareto || []} />
				)}
			</Box>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
